/**
 * hooks/useLayoutEditor.js — state container for the card editor.
 *
 * Chunk 3 + post-ship fix (Apr 24 2026):
 *   - Added changeBackdrop(kind) to mutate layout.basedOnTemplate via
 *     the same history stack (undo/redo friendly).
 */

import { useCallback, useMemo, useState } from 'react';

const HISTORY_LIMIT = 50;

export function useLayoutEditor() {
  const [layout, setLayout] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);

  const setInitialLayout = useCallback((initial) => {
    setLayout(clone(initial));
    setHistory([]);
    setFuture([]);
    setSelectedId(null);
    setDirty(false);
  }, []);

  const pushHistory = useCallback((prevLayout) => {
    setHistory((h) => {
      const next = [...h, prevLayout];
      if (next.length > HISTORY_LIMIT) next.shift();
      return next;
    });
    setFuture([]);
  }, []);

  const applyMutation = useCallback((mutator) => {
    setLayout((current) => {
      if (!current) return current;
      pushHistory(clone(current));
      setDirty(true);
      return mutator(clone(current));
    });
  }, [pushHistory]);

  const select = useCallback((id) => setSelectedId(id), []);

  const updateElement = useCallback((id, patch) => {
    applyMutation((draft) => {
      const idx = draft.elements.findIndex((e) => e.id === id);
      if (idx === -1) return draft;
      const prev = draft.elements[idx];
      draft.elements[idx] = {
        ...prev,
        ...patch,
        props: patch.props ? { ...prev.props, ...patch.props } : prev.props,
      };
      return draft;
    });
  }, [applyMutation]);

  const moveElement = useCallback((id, { x, y }) => {
    updateElement(id, { x, y });
  }, [updateElement]);

  const resizeElement = useCallback((id, { x, y, w, h }) => {
    updateElement(id, { x, y, w, h });
  }, [updateElement]);

  const deleteElement = useCallback((id) => {
    applyMutation((draft) => {
      draft.elements = draft.elements.filter((e) => e.id !== id);
      return draft;
    });
    setSelectedId((cur) => (cur === id ? null : cur));
  }, [applyMutation]);

  /**
   * Switch the layout's basedOnTemplate. This ONLY changes the
   * procedural backdrop (wave vs mountain/banner) — element
   * positions are preserved. User may need to reposition elements
   * that clash with the new backdrop.
   *
   * kind: 'minimal' | 'premium'
   */
  const changeBackdrop = useCallback((kind) => {
    if (kind !== 'minimal' && kind !== 'premium') return;
    applyMutation((draft) => {
      draft.basedOnTemplate = kind;
      return draft;
    });
  }, [applyMutation]);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setLayout((current) => {
        if (current) setFuture((f) => [...f, clone(current)]);
        setDirty(true);
        return clone(prev);
      });
      return h.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[f.length - 1];
      setLayout((current) => {
        if (current) setHistory((h) => [...h, clone(current)]);
        setDirty(true);
        return clone(next);
      });
      return f.slice(0, -1);
    });
  }, []);

  const markClean = useCallback(() => setDirty(false), []);

  const selectedElement = useMemo(() => {
    if (!layout || !selectedId) return null;
    return layout.elements.find((e) => e.id === selectedId) || null;
  }, [layout, selectedId]);

  return {
    layout,
    setInitialLayout,
    selectedId,
    selectedElement,
    select,
    updateElement,
    moveElement,
    resizeElement,
    deleteElement,
    changeBackdrop,
    undo,
    redo,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    dirty,
    markClean,
  };
}

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
